import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { UiKitModule } from '@serviceplan/ui-kit';
import { DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  AssetsGenerateUrlService,
  ClientEnvironment,
  CommonUtilityLib,
  ConfigObjectModel,
  ENVIRONMENT,
  ResizeService,
  ViewType,
  getRelationTypeName,
} from '@thehubz/client/core';
import { Asset, AssetsApiService, Collection, CollectionResultResponse, CollectionsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { BehaviorSubject, Subject, combineLatest, filter, map, skip, switchMap, takeUntil } from 'rxjs';
import { AssetsViewerBase } from '../../../base';
import { AssetCommonService } from '../../../services/asset-common.service';
import { AssetViewTypeService } from '../../../services/asset-view-type.service';
import { AssetEditPopupComponent } from '../../asset-edit-popup/asset-edit-popup.component';
import { AssetGalleryViewComponent } from '../../asset-gallery-view/asset-gallery-view.component';
import { AssetListViewComponent } from '../../asset-listview/asset-listview.component';
import { split } from '../../assets/assets-page.util';
import { CollectionsModule } from '../../collections/collections.module';
import { relationsListViewColumns } from './asset-relations.constants';
import { AssetRelationsService } from './asset-relations.service';
import { getRelationTypeColumnDef, groupAssetRelationsByRelationshipType } from './asset-relations.utils';

@Component({
  selector: 'thehubz-asset-relations',
  templateUrl: './asset-relations.component.html',
  styleUrls: ['./asset-relations.component.scss'],
  standalone: true,
  imports: [CommonModule, MatExpansionModule, MatDividerModule, AssetGalleryViewComponent, AssetListViewComponent, CollectionsModule, UiKitModule],
})
export class AssetRelationsComponent extends AssetsViewerBase implements OnInit, OnDestroy, AfterViewInit {
  relationsColumnDef = relationsListViewColumns;
  assetId$ = this.assetCommonService.assetDataSubject$.pipe(
    filter((asset) => !!asset && !this.commonUtilityLib.isEmptyObject(asset)),
    map((asset) => (asset as Asset)._id),
  );
  collectionList: (Collection | Record<string, AnyType>)[] = [];
  collectionListCount!: number;

  showEmptyState = false;
  viewType: ViewType = 'grid_4';
  viewType$ = new BehaviorSubject<ViewType>(this.viewType);
  collectionViewType = new BehaviorSubject<ViewType>('grid_4');

  sizes = AppSettings.SCREEN_SIZES;
  brandingKey!: CustomClients;
  prefix = 'is-';

  private destroy$ = new Subject<void>();
  dialogEditState?: number;
  dialogEditRef!: MatDialogRef<AssetEditPopupComponent>;
  configObject: ConfigObjectModel = {
    title: $localize`'Assets relations`,
    callFrom: 'assetRelations',
    hideDelete: true,
    hideDownload: false,
    hideAddToBasket: true,
    hideAddToCollection: true,
    hideSelection: true,
    hideShare: true,
  };

  /**
   * The asset related to the current asset.
   *
   * This is the response from the API call to get the related assets.
   */
  assetRelations$ = this.assetId$.pipe(
    switchMap((assetId) => this.assetsApiService.assetsControllerGetRelatedAssets(assetId)),
    map((assetRelations) =>
      assetRelations.map((assetRelation) => {
        assetRelation.relatedAsset = this.assetGenerateUrlService.addPreviewUrl(assetRelation.relatedAsset) as Asset;
        return assetRelation;
      }),
    ),
  );

  /**
   * The collection related to the current asset.
   */
  private _relatedCollections$ = this.assetId$.pipe(
    switchMap((assetId) => this.collectionAPI.collectionsControllerFindAll(`{"filters":{"assetId":"${assetId}"}}`)),
    map((collections) => collections.result),
  );

  /**
   * The related assets grouped by relationship type.
   */
  groupedAssetRelations$ = combineLatest([this.assetRelations$, this.viewType$]).pipe(
    map(([assetRelations, viewType]) => groupAssetRelationsByRelationshipType(assetRelations, viewType)),
  );

  combinedAssetRelations$ = combineLatest([this.groupedAssetRelations$, this._relatedCollections$]).pipe(
    map(([groupedAssetRelations, relatedCollections]) => ({
      groupedAssetRelations,
      relatedCollections,
    })),
  );

  assets$ = this.assetRelations$.pipe(
    map((assetRelations) =>
      assetRelations.map(
        (assetRelation) =>
          ({
            ...assetRelation.relatedAsset,
            relationType: getRelationTypeName(assetRelation.relationshipType.name),
          }) as AssetUIModel,
      ),
    ),
  );

  /**
   * Assets and collections related to the current asset.
   */
  tableData$ = this.assets$;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private router: Router,
    private elementRef: ElementRef,
    private resizeSvc: ResizeService,
    protected override dialog: MatDialog,
    private commonUtilityLib: CommonUtilityLib,
    private assetsApiService: AssetsApiService,
    private collectionAPI: CollectionsApiService,
    override assetCommonService: AssetCommonService,
    protected override assetViewTypeService: AssetViewTypeService,
    protected override assetRelationsService: AssetRelationsService,
    protected override downloadAssetsService: DownloadAssetsService,
    private readonly assetGenerateUrlService: AssetsGenerateUrlService,
  ) {
    super(assetCommonService, downloadAssetsService, dialog, assetRelationsService, assetViewTypeService);
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    this._initializeWheelMenu();
    this._initializeListViewColumns();
  }

  relationTypeName(relationKey: string) {
    return getRelationTypeName(relationKey);
  }

  private _initializeWheelMenu() {
    this.assetRelationsService.showWheelMenu(true);
    this._subscribeChangeViewTypeClick();
  }

  private _subscribeChangeViewTypeClick() {
    const customViewType = this.brandingKey === CustomClients.Vorwerk ? 'grid_8' : 'grid_4';
    this.changeViewType(customViewType);
    this.assetRelationsService.changeViewTypeClicked$.pipe(skip(1), takeUntil(this.destroy$)).subscribe((viewType) => {
      this.changeViewType(viewType);
    });
  }

  private changeViewType(viewType: ViewType) {
    this.changeAssetView(viewType);
    this.viewType = viewType;
    this.collectionViewType.next(viewType);
    this.viewType$.next(viewType);
  }

  private _initializeListViewColumns() {
    const relationsColumnDef = getRelationTypeColumnDef();
    const hasRelationTypeColumn = this.relationsColumnDef.TABLE_DEFAULT_COLUMNS.find((columnDef) => columnDef.columnDef === relationsColumnDef.columnDef);
    if (hasRelationTypeColumn) {
      return;
    }
    this.relationsColumnDef.TABLE_DEFAULT_COLUMNS.splice(relationsColumnDef.sequence, 0, relationsColumnDef);
  }

  private _collectionsAsAssetUIModel(collections: (Collection | Record<string, AnyType>)[]): Partial<AssetUIModel>[] {
    const collectionLabel = getRelationTypeName('collection');

    return collections.map((collection) => ({
      ...collection,
      relationType: collectionLabel,
      accessKeys: [],
      agency: collection.assets && collection.assets[0] && collection.assets[0].agency,
      assetType: Asset.AssetTypeEnum.Other,
      brand: collection.assets && collection.assets[0] && collection.assets[0].brand,
      createdAt: collection.createdAt,
      createdBy: collection.createdBy,
      client: collection.assets && collection.assets[0] && collection.assets[0].client,
      status: 'created',
      mainFile: undefined,
    }));
  }

  @HostListener('window:resize', [])
  private onResize() {
    this.detectScreenSize();
  }

  ngAfterViewInit(): void {
    this.detectScreenSize();
  }

  private detectScreenSize() {
    const currentSize = this.sizes.find((x) => {
      const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
      const isVisible = window.getComputedStyle(el).display != 'none';
      return isVisible;
    });
    this.resizeSvc.onResize(currentSize?.id);
    this.currentSize = currentSize;
  }

  editAssets(event: AnyType) {
    if (!this.dialogEditState && this.dialogEditState != 0) {
      this.dialogEditRef = this.dialog.open(AssetEditPopupComponent, {
        maxWidth: '120rem',
        width: '90%',
        height: '90%',
        data: event,
      });
      this.dialogEditState = this.dialogEditRef.getState();
      this.dialogEditRef.disableClose = true;
      this.dialogEditRef.afterClosed().subscribe(async (result: AssetUIModel[]) => {
        this.dialogEditState = undefined;
        if (result) {
          const currentUrl = this.router.url.split('?')[0];
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([currentUrl]);
          });
        }
      });
    }
  }

  updateSplitImageObject(imageObject: AssetUIModel[]) {
    const gridSize = parseInt(this.viewType.replace('grid_', ''));
    return split(imageObject, gridSize);
  }

  getGalleryHeight(assetCount: number) {
    const viewType = this.viewType;
    const size = this.currentSize.name;
    let height = '';

    if (viewType) {
      const calculateHeight = (rows: number) => {
        if (rows > 3) {
          height = '100%';
        } else {
          if (viewType === 'grid_8') {
            height = 215 * rows + 'px';
          } else if (viewType === 'grid_4') {
            height = 350 * rows + 'px';
          }
        }
      };

      switch (viewType) {
        case 'grid_4': {
          if (size === 'lg') {
            calculateHeight(Math.ceil(assetCount / 3));
          } else if (size === 'md') {
            calculateHeight(Math.ceil(assetCount / 2));
          } else {
            calculateHeight(Math.ceil(assetCount / 4));
          }
          break;
        }
        case 'grid_8': {
          if (size === 'md') {
            calculateHeight(Math.ceil(assetCount / 4));
          } else if (size === 'sm') {
            calculateHeight(Math.ceil(assetCount / 2));
          } else {
            calculateHeight(Math.ceil(assetCount / 8));
          }
          break;
        }
      }
    }

    return height;
  }

  handleCollectionList(collections: CollectionResultResponse) {
    this.collectionList = collections.result;
    this.collectionListCount = collections.count;
  }

  ngOnDestroy(): void {
    this.assetRelationsService.showWheelMenu(false);
    this.destroy$.next();
  }
}
